import { useEffect, useState, type ReactNode } from 'react'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import { Markdown } from '@tiptap/markdown'
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo2,
  Undo2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Toggle } from '@/components/ui/toggle'
import { cn } from '@/lib/utils'

type RichTextEditorProps = {
  /** Markdown content. */
  value: string
  /** Receives the content as Markdown; an empty editor yields ''. */
  onChange: (markdown: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

// WYSIWYG editor that stores its content as Markdown. Render the result with
// <MarkdownContent>.
export function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  className,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        strike: false,
        underline: false,
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          protocols: ['http', 'https', 'mailto'],
        },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      // `breaks` keeps single line breaks from older plain-text bodies.
      Markdown.configure({ markedOptions: { gfm: true, breaks: true } }),
    ],
    content: value,
    contentType: 'markdown',
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(getMarkdown(editor)),
    onBlur: () => onBlur?.(),
  })

  // Keep the editor in sync when the form is reset from outside.
  useEffect(() => {
    if (!editor || value === getMarkdown(editor)) return
    editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false })
  }, [editor, value])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [editor, disabled])

  useEffect(() => {
    editor?.setOptions({
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none min-h-[120px] px-3 py-2 focus:outline-none',
          role: 'textbox',
          'aria-multiline': 'true',
          ...(id ? { id } : {}),
          ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
          ...(ariaInvalid ? { 'aria-invalid': 'true' } : {}),
        },
      },
    })
  }, [editor, id, ariaDescribedBy, ariaInvalid])

  return (
    <div
      aria-invalid={ariaInvalid}
      className={cn(
        'w-full rounded-md border border-input bg-transparent shadow-sm focus-within:ring-1 focus-within:ring-ring aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/30',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {editor && <Toolbar editor={editor} disabled={disabled} />}
      <EditorContent editor={editor} />
    </div>
  )
}

function getMarkdown(editor: Editor) {
  return editor.isEmpty ? '' : editor.getMarkdown()
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      h2: editor.isActive('heading', { level: 2 }),
      h3: editor.isActive('heading', { level: 3 }),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      link: editor.isActive('link'),
      canUndo: editor.can().undo(),
      canRedo: editor.can().redo(),
    }),
  })

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-input p-1">
      <ToolbarToggle
        label="Fett"
        pressed={state.bold}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </ToolbarToggle>
      <ToolbarToggle
        label="Kursiv"
        pressed={state.italic}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </ToolbarToggle>
      <Separator orientation="vertical" className="mx-1 h-5" />
      <ToolbarToggle
        label="Überschrift"
        pressed={state.h2}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 />
      </ToolbarToggle>
      <ToolbarToggle
        label="Zwischenüberschrift"
        pressed={state.h3}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 />
      </ToolbarToggle>
      <ToolbarToggle
        label="Aufzählung"
        pressed={state.bulletList}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </ToolbarToggle>
      <ToolbarToggle
        label="Nummerierte Liste"
        pressed={state.orderedList}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolbarToggle>
      <LinkButton editor={editor} active={state.link} disabled={disabled} />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <ToolbarToggle
        label="Rückgängig"
        pressed={false}
        disabled={disabled || !state.canUndo}
        onPressedChange={() => editor.chain().focus().undo().run()}
      >
        <Undo2 />
      </ToolbarToggle>
      <ToolbarToggle
        label="Wiederholen"
        pressed={false}
        disabled={disabled || !state.canRedo}
        onPressedChange={() => editor.chain().focus().redo().run()}
      >
        <Redo2 />
      </ToolbarToggle>
    </div>
  )
}

function ToolbarToggle({
  label,
  pressed,
  disabled,
  onPressedChange,
  children,
}: {
  label: string
  pressed: boolean
  disabled: boolean
  onPressedChange: () => void
  children: ReactNode
}) {
  return (
    <Toggle
      size="sm"
      type="button"
      aria-label={label}
      title={label}
      pressed={pressed}
      disabled={disabled}
      // Keep focus and selection in the editor.
      onMouseDown={(event) => event.preventDefault()}
      onPressedChange={onPressedChange}
    >
      {children}
    </Toggle>
  )
}

function LinkButton({ editor, active, disabled }: { editor: Editor; active: boolean; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')

  const handleOpenChange = (next: boolean) => {
    if (next) setUrl(editor.getAttributes('link').href ?? '')
    setOpen(next)
  }

  const apply = () => {
    const href = url.trim()
    const chain = editor.chain().focus().extendMarkRange('link')
    if (!href) chain.unsetLink().run()
    // Without a selection there is nothing to link, so insert the URL itself.
    else if (editor.state.selection.empty && !active)
      chain.insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] }).run()
    else chain.setLink({ href }).run()
    setOpen(false)
  }

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Toggle size="sm" type="button" aria-label="Link" title="Link" pressed={active} disabled={disabled}>
          <Link2 />
        </Toggle>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3"
        // apply()/remove() focus the editor; don't move focus back to the trigger.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Input
          type="url"
          placeholder="https://…"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              apply()
            }
          }}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          {active && (
            <Button type="button" variant="ghost" size="sm" onClick={remove}>
              Entfernen
            </Button>
          )}
          <Button type="button" size="sm" onClick={apply}>
            Übernehmen
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
